import {ReactNode} from 'react';

import {CardModel} from '../../../src/models/api/cardModel';
import {
  buildContactFromContactUid,
  ContactModel,
  ContactSourcesEnum,
} from '../../../src/models/api/contactModel';
import {VisualContactModel} from '../../../src/models/api/visualContactModel';
import {compareName} from '../../helpers/localeHelpers';

/*
 * Constants.
 */

const visibleConversationsThreshold = 6;

/*
 * Types.
 */

// In the contact information section, we only show a subset of possible contact sources
export type ContactInformationContactSource =
  | ContactSourcesEnum.EMAIL
  | ContactSourcesEnum.PHONE
  | ContactSourcesEnum.TWITTER
  | ContactSourcesEnum.TWITTER_DM
  | ContactSourcesEnum.CUSTOM
  | ContactSourcesEnum.FACEBOOK;

export type ContactInformationContact = ContactModel & {source: ContactInformationContactSource};

export enum ConversationListRowTypesEnum {
  SUBTITLE = 'SUBTITLE',
  CONVERSATION = 'CONVERSATION',
}
export interface ConversationListRow {
  type: ConversationListRowTypesEnum;
  content: ReactNode;
}

/*
 * Helpers.
 */

const contactSourceOrdering = {
  [ContactSourcesEnum.EMAIL]: 1,
  [ContactSourcesEnum.PHONE]: 2,
  [ContactSourcesEnum.TWITTER]: 3,
  [ContactSourcesEnum.TWITTER_DM]: 3,
  [ContactSourcesEnum.CUSTOM]: 4,
  [ContactSourcesEnum.FACEBOOK]: 5,
};

// Use a type guard to narrow down the contact source
export function shouldDisplayContact(contact: ContactModel): contact is ContactInformationContact {
  return contact.source in contactSourceOrdering;
}

export function computeContacts(card: CardModel | undefined, contact?: VisualContactModel) {
  // If we do not have a card, return early
  if (!card) {
    return contact ? [contact.contact].filter(shouldDisplayContact) : [];
  }

  const contacts = card.contactUids.map(buildContactFromContactUid);

  // Filter out contacts from sources that we do not want to show
  const filteredContacts = contacts.filter(shouldDisplayContact);

  return filteredContacts.sort((c1, c2) => {
    // First sort based on the source, as defined in contactSourceOrdering
    const source1Order = contactSourceOrdering[c1.source];
    const source2Order = contactSourceOrdering[c2.source];
    if (source1Order !== source2Order) {
      return source1Order > source2Order ? 1 : -1;
    }

    // Then sort based on handle, with empty handles at the end
    const handle1 = c1.handle;
    const handle2 = c2.handle;
    if (c1.handle === '') {
      return 1;
    }
    if (c2.handle === '') {
      return -1;
    }
    return compareName(handle1, handle2);
  });
}

export function computeIsMainHandle(contact: ContactModel, visualContact?: VisualContactModel) {
  // We don't want the user to be able to modify the main contact that ties
  // P4 contact details to P2/P3
  return contact.uid === visualContact?.contact.uid;
}

export function splitIntoVisibleAndRemainingRows(rows: ReadonlyArray<ConversationListRow>) {
  let visibleConversationCount = 0;
  const lastVisibleIndex = rows.findIndex((row) => {
    if (row.type === ConversationListRowTypesEnum.CONVERSATION) {
      visibleConversationCount += 1;
    }

    return visibleConversationCount >= visibleConversationsThreshold;
  });

  const visibleRowCount = lastVisibleIndex !== -1 ? lastVisibleIndex + 1 : rows.length;

  return {
    visibleRows: rows.slice(0, visibleRowCount),
    remainingRows: rows.slice(visibleRowCount),
  };
}
